// Double tap and double click: the radius DOWN ONE RUNG OF THE RING LADDER
// (SPEC-viewfinder §1.2, the owner's ruling 10, re-ruled 2026-09-21).
//
// MapLibre's own `doubleClickZoom` is off, because it zooms by a whole level —
// half the radius — and the ruling is a rung. The rung is `ringBelow`, over
// `RING_LADDER`, which is the ladder the ring LABELS are built on: a resident
// who double taps at 3 km lands on the 2 km they can already read on the disc.
//
// The first ruling was one step of the slider. Measured, it was 0 m below about
// 1,5 km — finer than the 10 m `r` carries (ADR-0009) — so the gesture did
// nothing across half the range, and the owner re-ruled on that number.
//
// It is `'replace'`: a radius change opens nothing and writes no history entry
// (§1.7, ADR-0009), exactly as the slider does not.
import { viewState } from '../state/viewstate.ts';
import type { CameraDeps } from './camera.ts';
import { ringBelow } from './wheel-geometry.ts';

/** Two taps further apart than this are two taps. The platform's own figure. */
const DOUBLE_TAP_MS = 300;
/** And a thumb that moved further than this between them was pointing at two places. */
const DOUBLE_TAP_PX = 30;

export function hearDoubleTap(deps: CameraDeps): void {
  const container = deps.map.getCanvasContainer();
  /**
   * The previous tap, as the browser timed it — an event stamp, never a clock.
   * It starts infinitely long ago so the FIRST tap of a session can never read
   * as the second of a pair.
   */
  let last = Number.NEGATIVE_INFINITY;
  let at = { x: 0, y: 0 };
  /**
   * A step was already taken for the pointer sequence that is ending.
   *
   * A flag rather than a time window, and that is the point: a touch double tap
   * raises BOTH a synthesised `dblclick` and the pair counted below — measured
   * on Chrome, where one thumb stepped the radius twice — and the two have no
   * fixed order. Cleared by the next `pointerdown`, so the next deliberate
   * gesture is a fresh one however soon it comes. A window would have needed a
   * clock, and `e.timeStamp` stands still under the suite's pinned one, which
   * made a second double tap in a scenario do nothing at all.
   */
  let stepped = false;

  function tighten() {
    // The dial and a failed basemap offer no gesture at all (§6), and this is
    // one: the slider is still there for both.
    if (!deps.offered() || deps.disc() === 0 || stepped) return;
    stepped = true;
    const { radius } = viewState.get();
    // At the bottom rung `ringBelow` hands the radius back, and nothing is
    // written: a double tap at 200 m does nothing, which is the ruling.
    const rung = ringBelow(radius);
    if (rung !== radius) viewState.set({ radius: rung }, 'replace');
  }

  container.addEventListener('pointerdown', () => (stepped = false), true);

  // The desktop's own event, which the browser has already decided is a double
  // click. `preventDefault` keeps it from selecting the canvas underneath.
  container.addEventListener('dblclick', (e) => {
    e.preventDefault();
    tighten();
  });

  // A thumb cannot rely on `dblclick` everywhere, so the pair is counted here
  // too. The second tap of a pair does not start a third: `last` is cleared, or
  // three taps in 600 ms would step twice.
  container.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') return;
    const quick =
      e.timeStamp - last < DOUBLE_TAP_MS && Math.hypot(e.clientX - at.x, e.clientY - at.y) < DOUBLE_TAP_PX;
    last = quick ? Number.NEGATIVE_INFINITY : e.timeStamp;
    at = { x: e.clientX, y: e.clientY };
    if (quick) tighten();
  });
}
